import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Home, Settings, Eye, EyeOff, GripVertical, Save, ExternalLink,
  Plus, Trash2, X, Search, Check, Loader2, ChevronLeft, LogOut,
  Calendar, Users, MapPin, Image, MessageSquare, HelpCircle, Clock,
  BookOpen, PartyPopper, Palette, Upload, Star, FileText, Layout,
  ChevronDown, ChevronUp
} from 'lucide-react';
import CoupleNavigation from './CoupleNavigation';
import { wedding, coupleAuth, providers } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bridalconnect.co.za';

// ---------- Constants ----------

const SECTION_ICONS = {
  hero: Calendar,
  our_story: BookOpen,
  schedule: Clock,
  wedding_party: Users,
  travel: MapPin,
  gallery: Image,
  rsvp: MessageSquare,
  vendors: Star,
  faq: HelpCircle
};

const SECTION_LABELS = {
  hero: 'Hero / Header',
  our_story: 'Our Story',
  schedule: 'Schedule',
  wedding_party: 'Wedding Party',
  travel: 'Travel & Accommodation',
  gallery: 'Gallery',
  rsvp: 'RSVP',
  vendors: 'Vendors',
  faq: 'FAQ'
};

const PARTY_ROLES = [
  'Maid of Honour', 'Best Man', 'Bridesmaid', 'Groomsman',
  'Flower Girl', 'Ring Bearer', 'Other'
];

const COLOR_PALETTES = [
  { name: 'Protea Pink', primary: '#D4507A', secondary: '#F5E6CC' },
  { name: 'Bushveld Green', primary: '#5C7A3D', secondary: '#F0EDE5' },
  { name: 'Ocean Blue', primary: '#2E6B8A', secondary: '#E8F1F5' },
  { name: 'Sunset Gold', primary: '#D4913D', secondary: '#FFF8ED' },
  { name: 'Lavender Dream', primary: '#8B6BAE', secondary: '#F3EFF8' },
  { name: 'Classic Ivory', primary: '#8B7355', secondary: '#FFFFF0' },
  { name: 'Rose Garden', primary: '#C75B7A', secondary: '#FFF0F3' },
  { name: 'Midnight Elegance', primary: '#2C3E50', secondary: '#F8F9FA' }
];

const TEMPLATES = [
  { id: 'classic', name: 'Classic', desc: 'Timeless elegance with serif fonts', preview: 'bg-gradient-to-br from-amber-50 to-rose-50' },
  { id: 'modern', name: 'Modern', desc: 'Clean lines, sans-serif, minimal', preview: 'bg-gradient-to-br from-gray-50 to-slate-100' },
  { id: 'rustic', name: 'Rustic', desc: 'Warm tones, natural textures', preview: 'bg-gradient-to-br from-orange-50 to-amber-100' }
];

const SA_PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape'
];

// ---------- Sidebar Navigation ----------

function Sidebar({ page, navigate }) {
  const couple = coupleAuth.getCurrentUser();
  const handleLogout = () => {
    coupleAuth.logout();
    navigate('/couple/login');
  };

  return (
    <div className="w-56 bg-white border-r border-gray-200 flex flex-col min-h-screen">
      <div className="p-4 border-b border-gray-200">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/')}
        >
          <div className="w-7 h-7 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
            <Heart className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            BridalConnect
          </span>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1">
        <SidebarLink icon={Layout} label="Page Builder" active onClick={() => {}} />
        <SidebarLink icon={Users} label="Guest List" onClick={() => navigate('/couple/guests')} />
        <SidebarLink icon={MessageSquare} label="RSVPs" onClick={() => navigate('/couple/rsvps')} />
        <SidebarLink icon={Layout} label="Dashboard" onClick={() => navigate('/couple/dashboard')} />
      </nav>

      <div className="p-3 border-t border-gray-200">
        {couple && (
          <p className="text-xs text-gray-500 mb-2 truncate px-2">{couple.email}</p>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <LogOut className="w-4 h-4" />
          Log Out
        </button>
      </div>
    </div>
  );
}

function SidebarLink({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
        active
          ? 'bg-purple-50 text-purple-700 font-medium'
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// ---------- Section Editors ----------

function HeroEditor({ section, onChange, page, onPageUpdate }) {
  const content = section.content || {};
  const [uploading, setUploading] = useState(false);
  const [heroPreview, setHeroPreview] = useState(page?.hero_image_url || null);
  const heroFileRef = useRef();

  const updateContent = (field, value) => {
    onChange({ ...section, content: { ...content, [field]: value } });
  };
  // Also save to page-level fields so the public page can read them
  const updatePageField = (field, value) => {
    updateContent(field, value);
    if (onPageUpdate) onPageUpdate({ [field]: value });
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('hero', file);
      const res = await wedding.uploadHero(formData);
      const url = res.data?.hero_image_url;
      if (url) {
        setHeroPreview(url);
        if (onPageUpdate) onPageUpdate({ hero_image_url: url });
      }
    } catch (err) {
      console.error('Hero upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const heroUrl = heroPreview
    ? (heroPreview.startsWith('http') ? heroPreview : `${API_URL}${heroPreview}`)
    : null;

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-gray-800">Hero / Header</h3>
      <p className="text-sm text-gray-500">Upload a cover photo and set your wedding details.</p>

      {/* Hero image upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Cover Photo</label>
        <input type="file" ref={heroFileRef} accept="image/*" onChange={handleHeroUpload} className="hidden" />
        {heroUrl ? (
          <div className="relative rounded-xl overflow-hidden">
            <img src={heroUrl} alt="Hero" className="w-full h-48 object-cover" />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <button
                onClick={() => heroFileRef.current?.click()}
                disabled={uploading}
                className="px-4 py-2 bg-white/90 text-gray-800 rounded-lg text-sm font-medium hover:bg-white flex items-center gap-2"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Change Photo
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => heroFileRef.current?.click()}
            disabled={uploading}
            className="w-full h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-purple-300 hover:text-purple-500 hover:bg-purple-50 transition-all"
          >
            {uploading ? (
              <><Loader2 className="w-6 h-6 animate-spin mb-2" /><span className="text-sm">Uploading...</span></>
            ) : (
              <><Image className="w-8 h-8 mb-2" /><span className="text-sm font-medium">Upload a cover photo</span><p className="text-xs mt-1">This will be the background of your hero section</p></>
            )}
          </button>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Wedding Date</label>
        <input
          type="date"
          value={page?.wedding_date || content.wedding_date || ''}
          onChange={(e) => updatePageField('wedding_date', e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name</label>
        <input
          type="text"
          value={page?.venue_name || content.venue_name || ''}
          onChange={(e) => updatePageField('venue_name', e.target.value)}
          placeholder="e.g. The Oyster Box"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Venue City</label>
        <input
          type="text"
          value={page?.venue_city || content.venue_city || ''}
          onChange={(e) => updatePageField('venue_city', e.target.value)}
          placeholder="e.g. Umhlanga"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
        <select
          value={page?.venue_province || content.venue_province || ''}
          onChange={(e) => updatePageField('venue_province', e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="">Select province</option>
          {SA_PROVINCES.map(p => (
            <option key={p} value={p} disabled={p !== 'KwaZulu-Natal'} className={p !== 'KwaZulu-Natal' ? 'text-gray-300' : ''}>
              {p}{p !== 'KwaZulu-Natal' ? ' (coming soon)' : ''}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function StoryEditor({ section, onChange }) {
  const content = section.content || {};
  const update = (value) => {
    onChange({ ...section, content: { ...content, text: value } });
  };

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-gray-800">Our Story</h3>
      <p className="text-sm text-gray-500">Tell your guests how you met and your journey together.</p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Your Story</label>
        <textarea
          value={content.text || ''}
          onChange={(e) => update(e.target.value)}
          rows={10}
          placeholder="Tell your love story..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y"
        />
        <p className="text-xs text-gray-400 mt-1">{(content.text || '').length} characters</p>
      </div>
    </div>
  );
}

function ScheduleEditor({ section }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const loadEvents = useCallback(async () => {
    try {
      const res = await wedding.getEvents();
      setEvents(res.data.events || res.data || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const emptyForm = { name: '', date: '', start_time: '', end_time: '', venue: '', address: '', dress_code: '', description: '' };

  const openNew = () => {
    setForm(emptyForm);
    setEditing('new');
  };

  const openEdit = (ev) => {
    setForm({
      name: ev.name || '',
      date: ev.date ? ev.date.split('T')[0] : '',
      start_time: ev.start_time || '',
      end_time: ev.end_time || '',
      venue: ev.venue || '',
      address: ev.address || '',
      dress_code: ev.dress_code || '',
      description: ev.description || ''
    });
    setEditing(ev.id);
  };

  const saveEvent = async () => {
    try {
      if (editing === 'new') {
        await wedding.createEvent(form);
      } else {
        await wedding.updateEvent(editing, form);
      }
      setEditing(null);
      loadEvents();
    } catch (err) {
      console.error('Failed to save event:', err);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await wedding.deleteEvent(id);
      loadEvents();
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading events...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Schedule</h3>
          <p className="text-sm text-gray-500">Add your wedding day events and timeline.</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          <Plus className="w-4 h-4" /> Add Event
        </button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-sm text-gray-700">{editing === 'new' ? 'New Event' : 'Edit Event'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Event Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ceremony" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
                <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Venue</label>
              <input type="text" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })}
                placeholder="Venue name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Dress Code</label>
              <input type="text" value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })}
                placeholder="e.g. Black Tie" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
              <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Full address" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} placeholder="Optional details" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Cancel</button>
            <button onClick={saveEvent} disabled={!form.name} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">Save</button>
          </div>
        </div>
      )}

      {events.length === 0 && !editing && (
        <p className="text-sm text-gray-400 py-4 text-center">No events yet. Add your first event above.</p>
      )}

      <div className="space-y-2">
        {events.map((ev) => (
          <div key={ev.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div>
              <p className="font-medium text-sm text-gray-800">{ev.name}</p>
              <p className="text-xs text-gray-500">
                {ev.date && new Date(ev.date).toLocaleDateString('en-ZA')}
                {ev.start_time && ` at ${ev.start_time}`}
                {ev.venue && ` - ${ev.venue}`}
              </p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(ev)} className="p-1.5 text-gray-400 hover:text-purple-600 rounded">
                <FileText className="w-4 h-4" />
              </button>
              <button onClick={() => deleteEvent(ev.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartyEditor({ section }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const fileRef = useRef();

  const loadParty = useCallback(async () => {
    try {
      const res = await wedding.getParty();
      setMembers(res.data.party || res.data || []);
    } catch (err) {
      console.error('Failed to load party:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadParty(); }, [loadParty]);

  const emptyForm = { name: '', role: '', side: 'shared', bio: '' };

  const openNew = () => {
    setForm(emptyForm);
    setPhotoFile(null);
    setEditing('new');
  };

  const openEdit = (m) => {
    setForm({ name: m.name || '', role: m.role || '', side: m.side || 'shared', bio: m.bio || '' });
    setPhotoFile(null);
    setEditing(m.id);
  };

  const saveMember = async () => {
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photoFile) fd.append('photo', photoFile);

      if (editing === 'new') {
        await wedding.addPartyMember(fd);
      } else {
        await wedding.updatePartyMember(editing, fd);
      }
      setEditing(null);
      setPhotoFile(null);
      loadParty();
    } catch (err) {
      console.error('Failed to save party member:', err);
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm('Remove this party member?')) return;
    try {
      await wedding.deletePartyMember(id);
      loadParty();
    } catch (err) {
      console.error('Failed to delete party member:', err);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading wedding party...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Wedding Party</h3>
          <p className="text-sm text-gray-500">Add your bridal party and groomsmen.</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {editing && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-sm text-gray-700">{editing === 'new' ? 'New Member' : 'Edit Member'}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Select role</option>
                {PARTY_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Side</label>
              <select value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="partner1">Partner 1</option>
                <option value="partner2">Partner 2</option>
                <option value="shared">Shared</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Bio</label>
              <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={2} placeholder="A short bio..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Photo</label>
              <input type="file" accept="image/*" ref={fileRef} onChange={(e) => setPhotoFile(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-purple-50 file:text-purple-700" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setEditing(null); setPhotoFile(null); }} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Cancel</button>
            <button onClick={saveMember} disabled={!form.name} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">Save</button>
          </div>
        </div>
      )}

      {members.length === 0 && !editing && (
        <p className="text-sm text-gray-400 py-4 text-center">No party members yet.</p>
      )}

      <div className="grid grid-cols-2 gap-3">
        {members.map((m) => (
          <div key={m.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-start gap-3">
            {m.photo_url ? (
              <img src={m.photo_url.startsWith('http') ? m.photo_url : `${API_URL}${m.photo_url}`} alt={m.name}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-gray-800 truncate">{m.name}</p>
              <p className="text-xs text-gray-500">{m.role || 'No role'}</p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => openEdit(m)} className="p-1 text-gray-400 hover:text-purple-600"><FileText className="w-3.5 h-3.5" /></button>
              <button onClick={() => deleteMember(m.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TravelEditor({ section, onChange }) {
  const content = section.content || {};
  const items = content.items || [];

  const updateItems = (newItems) => {
    onChange({ ...section, content: { ...content, items: newItems } });
  };

  const addItem = () => {
    updateItems([...items, { name: '', address: '', notes: '', map_url: '' }]);
  };

  const updateItem = (idx, field, value) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    updateItems(updated);
  };

  const removeItem = (idx) => {
    updateItems(items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Travel & Accommodation</h3>
          <p className="text-sm text-gray-500">Help your guests with accommodation and travel info.</p>
        </div>
        <button onClick={addItem} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          <Plus className="w-4 h-4" /> Add Place
        </button>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-gray-400 py-4 text-center">No travel items yet.</p>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Place {idx + 1}</span>
              <button onClick={() => removeItem(idx)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input type="text" value={item.name || ''} onChange={(e) => updateItem(idx, 'name', e.target.value)}
                  placeholder="e.g. Hilton Hotel" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Map URL</label>
                <input type="url" value={item.map_url || ''} onChange={(e) => updateItem(idx, 'map_url', e.target.value)}
                  placeholder="Google Maps link" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                <input type="text" value={item.address || ''} onChange={(e) => updateItem(idx, 'address', e.target.value)}
                  placeholder="Full address" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea value={item.notes || ''} onChange={(e) => updateItem(idx, 'notes', e.target.value)}
                  rows={2} placeholder="Booking instructions, discount codes, etc." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryEditor({ section }) {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const loadMedia = useCallback(async () => {
    try {
      const res = await wedding.getMedia();
      setMediaList(res.data.media || res.data || []);
    } catch (err) {
      console.error('Failed to load media:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('photo', file);
        await wedding.uploadMedia(fd);
      }
      loadMedia();
    } catch (err) {
      console.error('Failed to upload:', err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await wedding.deleteMedia(id);
      loadMedia();
    } catch (err) {
      console.error('Failed to delete media:', err);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading gallery...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Gallery</h3>
          <p className="text-sm text-gray-500">Upload photos to your wedding gallery.</p>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading...' : 'Upload Photos'}
        </button>
        <input type="file" ref={fileRef} accept="image/*" multiple onChange={handleUpload} className="hidden" />
      </div>

      {mediaList.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <Image className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No photos yet. Upload your first photo.</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {mediaList.map((m) => {
          const src = m.url ? (m.url.startsWith('http') ? m.url : `${API_URL}${m.url}`) : '';
          return (
            <div key={m.id} className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <button onClick={() => deletePhoto(m.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-red-600 text-white rounded-full hover:bg-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RSVPEditor() {
  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-gray-800">RSVP</h3>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
        <MessageSquare className="w-10 h-10 text-purple-400 mx-auto mb-3" />
        <p className="text-sm text-gray-700 font-medium mb-1">RSVP section is automatically generated</p>
        <p className="text-sm text-gray-500">Manage your guest list to customize the RSVP experience. Each guest receives a unique RSVP link.</p>
        <a href="/couple/guests" className="inline-block mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium">
          Go to Guest List &rarr;
        </a>
      </div>
    </div>
  );
}

function VendorEditor({ section }) {
  const [linkedVendors, setLinkedVendors] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const searchTimeout = useRef();

  const loadVendors = useCallback(async () => {
    try {
      const res = await wedding.getVendors();
      setLinkedVendors(res.data.vendors || res.data || []);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  const handleSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await providers.getAll({ search: q, limit: 10 });
      setSearchResults(res.data.providers || res.data || []);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  const onSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearch(q), 400);
  };

  const addVendor = async (provider) => {
    try {
      await wedding.addVendor({ provider_id: provider.id, role_label: provider.category_name || '' });
      setSearchQuery('');
      setSearchResults([]);
      loadVendors();
    } catch (err) {
      console.error('Failed to add vendor:', err);
    }
  };

  const addCustomVendor = async () => {
    if (!customName.trim()) return;
    setAdding(true);
    try {
      await wedding.addVendor({
        custom_name: customName.trim(),
        role_label: customRole.trim(),
        custom_url: customUrl.trim() || null
      });
      setCustomName('');
      setCustomRole('');
      setCustomUrl('');
      setShowCustomForm(false);
      loadVendors();
    } catch (err) {
      console.error('Failed to add custom vendor:', err);
    } finally {
      setAdding(false);
    }
  };

  const removeVendor = async (id) => {
    if (!window.confirm('Remove this vendor?')) return;
    try {
      await wedding.removeVendor(id);
      loadVendors();
    } catch (err) {
      console.error('Failed to remove vendor:', err);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading vendors...</div>;

  const linkedIds = new Set(linkedVendors.map(v => v.provider_id || v.id));

  return (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-gray-800">Your Wedding Vendors</h3>
      <p className="text-sm text-gray-500">Add the vendors you've booked for your wedding. Search BridalConnect or add any vendor manually.</p>

      {/* Search BridalConnect */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Search BridalConnect Directory</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={searchQuery} onChange={onSearchChange}
            placeholder="Search providers by name..."
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
        </div>
      </div>

      {searchResults.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">
          {searchResults.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-4 py-2.5">
              <div>
                <p className="text-sm font-medium text-gray-800">{p.business_name || p.name}</p>
                <p className="text-xs text-gray-500">{p.category_name || p.category || ''}</p>
              </div>
              {linkedIds.has(p.id) ? (
                <span className="text-xs text-green-600 font-medium flex items-center gap-1"><Check className="w-3 h-3" /> Added</span>
              ) : (
                <button onClick={() => addVendor(p)} className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium">
                  Add
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
        <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-gray-400">or</span></div>
      </div>

      {/* Add Custom Vendor */}
      {!showCustomForm ? (
        <button
          onClick={() => setShowCustomForm(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-purple-300 hover:text-purple-600 hover:bg-purple-50 transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add a vendor not on BridalConnect
        </button>
      ) : (
        <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
          <p className="text-sm font-medium text-gray-700">Add Custom Vendor</p>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Vendor / Business Name *</label>
            <input type="text" value={customName} onChange={(e) => setCustomName(e.target.value)}
              placeholder="e.g. Sarah's Flowers"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">What they do (e.g. Florist, DJ, Photographer)</label>
            <input type="text" value={customRole} onChange={(e) => setCustomRole(e.target.value)}
              placeholder="e.g. Florist"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Website or Social Media Link</label>
            <input type="url" value={customUrl} onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="e.g. https://instagram.com/sarahsflowers"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setShowCustomForm(false); setCustomName(''); setCustomRole(''); setCustomUrl(''); }}
              className="flex-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={addCustomVendor} disabled={!customName.trim() || adding}
              className="flex-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-1">
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Add Vendor
            </button>
          </div>
        </div>
      )}

      {/* Linked vendors list */}
      {linkedVendors.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Your Vendors ({linkedVendors.length})</label>
          <div className="space-y-2">
            {linkedVendors.map((v) => (
              <div key={v.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm text-gray-800 truncate">{v.business_name || v.custom_name || 'Vendor'}</p>
                    {v.provider_id ? (
                      <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">BridalConnect</span>
                    ) : (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">Custom</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{v.role_label || v.category_name || ''}</p>
                  {v.custom_url && <p className="text-xs text-purple-500 truncate">{v.custom_url}</p>}
                </div>
                <button onClick={() => removeVendor(v.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded ml-2 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {linkedVendors.length === 0 && !showCustomForm && (
        <p className="text-sm text-gray-400 py-2 text-center">No vendors added yet.</p>
      )}
    </div>
  );
}

function FAQEditor({ section, onChange }) {
  const content = section.content || {};
  const items = content.items || [];

  const updateItems = (newItems) => {
    onChange({ ...section, content: { ...content, items: newItems } });
  };

  const addItem = () => {
    updateItems([...items, { question: '', answer: '' }]);
  };

  const updateItem = (idx, field, value) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    updateItems(updated);
  };

  const removeItem = (idx) => {
    updateItems(items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">FAQ</h3>
          <p className="text-sm text-gray-500">Common questions and answers for your guests.</p>
        </div>
        <button onClick={addItem} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          <Plus className="w-4 h-4" /> Add Question
        </button>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-gray-400 py-4 text-center">No FAQ items yet.</p>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Question {idx + 1}</span>
              <button onClick={() => removeItem(idx)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Question</label>
              <input type="text" value={item.question || ''} onChange={(e) => updateItem(idx, 'question', e.target.value)}
                placeholder="e.g. Is there parking available?" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Answer</label>
              <textarea value={item.answer || ''} onChange={(e) => updateItem(idx, 'answer', e.target.value)}
                rows={2} placeholder="Your answer..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Page Settings Panel ----------

function PageSettings({ page, onUpdate, onClose }) {
  const colorScheme = page.color_scheme || {};
  const [template, setTemplate] = useState(page.template || 'classic');
  const [palette, setPalette] = useState('');
  const [customPrimary, setCustomPrimary] = useState(colorScheme.primary || '#D4507A');
  const [customSecondary, setCustomSecondary] = useState(colorScheme.secondary || '#F5E6CC');
  const [useCustom, setUseCustom] = useState(false);

  // Detect which palette matches current colors
  useState(() => {
    const match = COLOR_PALETTES.find(p => p.primary === colorScheme.primary && p.secondary === colorScheme.secondary);
    if (match) setPalette(match.name);
    else setUseCustom(true);
  });

  const handleSave = () => {
    const selectedPalette = COLOR_PALETTES.find(p => p.name === palette);
    const primary = useCustom ? customPrimary : (selectedPalette?.primary || customPrimary);
    const secondary = useCustom ? customSecondary : (selectedPalette?.secondary || customSecondary);
    onUpdate({
      template,
      color_scheme: {
        primary,
        secondary,
        accent: primary,
        background: secondary
      }
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Page Settings</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Template</label>
            <div className="grid grid-cols-3 gap-3">
              {TEMPLATES.map(t => (
                <button key={t.id} onClick={() => setTemplate(t.id)}
                  className={`rounded-lg border-2 p-3 text-left transition-colors ${
                    template === t.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className={`w-full h-16 rounded-md mb-2 ${t.preview}`} />
                  <p className="text-sm font-medium text-gray-800">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Color Scheme */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Color Scheme</label>
            <div className="grid grid-cols-2 gap-2">
              {COLOR_PALETTES.map(p => (
                <button key={p.name} onClick={() => { setPalette(p.name); setUseCustom(false); }}
                  className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2.5 transition-colors ${
                    palette === p.name && !useCustom ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className="flex gap-1 flex-shrink-0">
                    <div className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: p.primary }} />
                    <div className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: p.secondary }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{p.name}</span>
                </button>
              ))}
            </div>

            <div className="mt-3">
              <button onClick={() => setUseCustom(!useCustom)}
                className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 w-full transition-colors ${
                  useCustom ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <Palette className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-700">Custom Colors</span>
              </button>
              {useCustom && (
                <div className="mt-2 flex gap-4 pl-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Primary</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={customPrimary} onChange={(e) => setCustomPrimary(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border-0" />
                      <input type="text" value={customPrimary} onChange={(e) => setCustomPrimary(e.target.value)}
                        className="w-24 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Secondary</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={customSecondary} onChange={(e) => setCustomSecondary(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border-0" />
                      <input type="text" value={customSecondary} onChange={(e) => setCustomSecondary(e.target.value)}
                        className="w-24 border border-gray-300 rounded px-2 py-1 text-xs font-mono" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save Settings</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Main PageBuilder ----------

function PageBuilder() {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState(null);

  const saveTimerRef = useRef();
  const pendingSaveRef = useRef(null);

  // Load page data
  useEffect(() => {
    const load = async () => {
      try {
        const res = await wedding.getMyPage();
        const data = res.data.page || res.data;
        setPage(data);
        const secs = data.sections || [];
        // Ensure all section types exist
        const sectionTypes = ['hero', 'our_story', 'schedule', 'wedding_party', 'travel', 'gallery', 'rsvp', 'vendors', 'faq'];
        const existing = secs.map(s => s.section_type);
        const full = [...secs];
        sectionTypes.forEach(type => {
          if (!existing.includes(type)) {
            full.push({ section_type: type, title: SECTION_LABELS[type] || type, is_visible: true, content: {}, sort_order: full.length });
          }
        });
        setSections(full);
      } catch (err) {
        console.error('Failed to load page:', err);
        if (err.response?.status === 401) {
          navigate('/couple/login');
          return;
        }
        setError('Failed to load your wedding page. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  // Auto-save with debounce
  const triggerSave = useCallback((sectionData) => {
    clearTimeout(saveTimerRef.current);
    pendingSaveRef.current = sectionData;
    setSaveStatus('saving');

    saveTimerRef.current = setTimeout(async () => {
      try {
        if (sectionData.id) {
          await wedding.updateSection(sectionData.id, {
            content: sectionData.content,
            is_visible: sectionData.is_visible,
            title: sectionData.title
          });
        } else {
          // For sections without an id, update via bulk
          await wedding.updateSections(
            sections.map(s => s.section_type === sectionData.section_type ? sectionData : s)
          );
        }
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus('idle');
      }
    }, 1500);
  }, [sections]);

  // Handle section content change (from editors)
  const handleSectionChange = useCallback((updatedSection) => {
    setSections(prev => {
      const next = prev.map((s, i) => i === selectedIdx ? updatedSection : s);
      return next;
    });
    triggerSave(updatedSection);
  }, [selectedIdx, triggerSave]);

  // Toggle visibility
  const toggleVisibility = useCallback(async (idx) => {
    setSections(prev => {
      const next = prev.map((s, i) => i === idx ? { ...s, is_visible: !s.is_visible } : s);
      const updated = next[idx];
      // Fire save for this section
      if (updated.id) {
        wedding.updateSection(updated.id, { is_visible: updated.is_visible }).catch(console.error);
      }
      return next;
    });
  }, []);

  // Drag reorder
  const [dragIdx, setDragIdx] = useState(null);

  const handleDragStart = (idx) => {
    setDragIdx(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setSections(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
    if (selectedIdx === dragIdx) setSelectedIdx(idx);
  };

  const handleDragEnd = async () => {
    setDragIdx(null);
    try {
      const ordered = sections.map((s, i) => ({ ...s, display_order: i }));
      await wedding.updateSections(ordered);
    } catch (err) {
      console.error('Failed to save section order:', err);
    }
  };

  // Page settings update
  const handleSettingsUpdate = async (updates) => {
    try {
      await wedding.updateMyPage(updates);
      setPage(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error('Failed to save settings:', err);
    }
  };

  // Preview
  const openPreview = () => {
    navigate('/couple/preview');
  };

  // Render section editor
  const renderEditor = () => {
    if (sections.length === 0) return null;
    const section = sections[selectedIdx];
    if (!section) return null;

    const editorProps = { section, onChange: handleSectionChange };

    switch (section.section_type) {
      case 'hero': return <HeroEditor {...editorProps} page={page} onPageUpdate={handleSettingsUpdate} />;
      case 'our_story': return <StoryEditor {...editorProps} />;
      case 'schedule': return <ScheduleEditor {...editorProps} />;
      case 'wedding_party': return <PartyEditor {...editorProps} />;
      case 'travel': return <TravelEditor {...editorProps} />;
      case 'gallery': return <GalleryEditor {...editorProps} />;
      case 'rsvp': return <RSVPEditor />;
      case 'vendors': return <VendorEditor {...editorProps} />;
      case 'faq': return <FAQEditor {...editorProps} />;
      default: return <p className="text-sm text-gray-400">No editor available for this section type.</p>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading your page builder...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <p className="text-red-600 font-medium mb-2">Error</p>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const coupleData = {
    partner1_name: page?.partner1_name,
    partner2_name: page?.partner2_name,
    slug: page?.slug
  };

  const handlePublishToggle = async () => {
    try {
      const newState = !page?.is_published;
      await wedding.publishPage(newState);
      setPage(prev => ({ ...prev, is_published: newState }));
    } catch (err) {
      console.error('Publish error:', err);
    }
  };

  return (
    <CoupleNavigation couple={coupleData} isPublished={page?.is_published} onPublishToggle={handlePublishToggle}>
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-gray-800">Page Builder</h1>
            {page?.slug && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full font-mono">
                /{page.slug}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Save status */}
            <span className={`text-xs flex items-center gap-1 transition-opacity ${
              saveStatus === 'idle' ? 'opacity-0' : 'opacity-100'
            }`}>
              {saveStatus === 'saving' && <><Loader2 className="w-3 h-3 animate-spin text-gray-400" /> <span className="text-gray-400">Saving...</span></>}
              {saveStatus === 'saved' && <><Check className="w-3 h-3 text-green-500" /> <span className="text-green-600">Saved</span></>}
            </span>

            {/* Settings */}
            <button onClick={() => setShowSettings(true)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Page Settings">
              <Settings className="w-5 h-5" />
            </button>

            {/* Preview */}
            <button onClick={openPreview}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
              <ExternalLink className="w-4 h-4" /> Preview
            </button>
          </div>
        </div>

        {/* Builder area */}
        <div className="flex-1 flex">
          {/* Section list panel */}
          <div className="w-72 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Sections</h2>
            </div>
            <div className="p-2">
              {sections.map((section, idx) => {
                const Icon = SECTION_ICONS[section.section_type] || FileText;
                const isSelected = idx === selectedIdx;
                return (
                  <div
                    key={section.section_type}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedIdx(idx)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors mb-0.5 group ${
                      isSelected
                        ? 'bg-purple-50 border border-purple-200'
                        : dragIdx === idx
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <GripVertical className="w-4 h-4 text-gray-300 cursor-grab flex-shrink-0" />
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                    <span className={`text-sm flex-1 truncate ${isSelected ? 'font-medium text-purple-700' : 'text-gray-700'}`}>
                      {SECTION_LABELS[section.section_type] || section.title || section.section_type}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleVisibility(idx); }}
                      className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-200"
                      title={section.is_visible ? 'Hide section' : 'Show section'}
                    >
                      {section.is_visible !== false ? (
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5 text-red-400" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Editor panel */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-2xl mx-auto p-8">
              {sections[selectedIdx] && !sections[selectedIdx].is_visible && sections[selectedIdx].is_visible !== undefined && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <p className="text-sm text-amber-700">This section is hidden from visitors. Toggle visibility in the section list.</p>
                </div>
              )}
              {renderEditor()}
            </div>
          </div>
        </div>
      </div>

      {/* Settings modal */}
      {showSettings && (
        <PageSettings page={page} onUpdate={handleSettingsUpdate} onClose={() => setShowSettings(false)} />
      )}
    </CoupleNavigation>
  );
}

export default PageBuilder;
